import React, { useState, useEffect, useCallback } from 'react';
import { getReports } from '../utils/api';

const NAVY = '#0d2137';

function fmt(n) { return Number(n || 0).toLocaleString('en-PK'); }
function fmtPKR(n) { return 'PKR ' + Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 0 }); }

function getToday() { return new Date().toISOString().slice(0, 10); }

function getRange(type) {
    const now = new Date();
    if (type === 'daily') {
        const d = getToday();
        return { from: d, to: d };
    }
    if (type === 'weekly') {
        const day = now.getDay();
        const mon = new Date(now); mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return { from: mon.toISOString().slice(0, 10), to: sun.toISOString().slice(0, 10) };
    }
    if (type === 'monthly') {
        const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
        const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
        return { from, to };
    }
    return { from: getToday(), to: getToday() };
}

function SummaryCard({ icon, label, value, color, bg }) {
    return (
        <div style={{ background: bg || '#fff', border: `1px solid ${color}33`, borderRadius: 12,
            padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 32 }}>{icon}</div>
            <div>
                <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{label}</div>
            </div>
        </div>
    );
}

function printReport(data, from, to, tabLabel) {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { alert('Popup blocked — allow popups and try again.'); return; }
    const s = data.summary;
    const rows = (data.doctor_wise || []).map(r =>
        `<tr><td>${r.doctor_name || 'Walk-in'}</td><td>${r.specialty || '—'}</td>
         <td style="text-align:right">${r.visits}</td>
         <td style="text-align:right">PKR ${Number(r.revenue).toLocaleString('en-PK')}</td></tr>`
    ).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
        body{font-family:Arial,sans-serif;padding:28px;color:#111}
        h1{color:#0d2137;font-size:20px;margin-bottom:2px}
        h2{color:#64748b;font-size:13px;font-weight:normal;margin-bottom:18px}
        .grid{display:flex;gap:16px;margin-bottom:20px;flex-wrap:wrap}
        .card{border:1px solid #e2e8f0;border-radius:8px;padding:14px 18px;min-width:130px}
        .num{font-size:22px;font-weight:800;color:#0d2137}
        .lbl{font-size:11px;color:#64748b;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{background:#0d2137;color:#fff;padding:9px 12px;text-align:left}
        td{padding:8px 12px;border-bottom:1px solid #e2e8f0}
        tr:nth-child(even) td{background:#f8fafc}
        .sec{font-size:13px;font-weight:700;color:#0d2137;margin:18px 0 8px}
        .footer{margin-top:28px;font-size:10px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:10px}
        @media print{@page{margin:15mm}}
    </style></head><body>
    <h1>🏥 DHA Medical Centre — ${tabLabel} Report</h1>
    <h2>Period: ${from} to ${to} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString('en-PK')}</h2>
    <div class="grid">
        <div class="card"><div class="num">${fmt(s.visits)}</div><div class="lbl">Total Visits</div></div>
        <div class="card"><div class="num">${fmt(s.new_patients)}</div><div class="lbl">New Patients</div></div>
        <div class="card"><div class="num">${fmtPKR(s.revenue)}</div><div class="lbl">Revenue (Consultation)</div></div>
    </div>
    <div class="sec">Doctor-wise Breakdown</div>
    <table>
        <tr><th>Doctor</th><th>Specialty</th><th style="text-align:right">Visits</th><th style="text-align:right">Revenue</th></tr>
        ${rows || '<tr><td colspan="4" style="text-align:center;color:#94a3b8">No visits in this period</td></tr>'}
    </table>
    <div class="footer">DHA Medical Centre — In Collaboration with CARELINE — Confidential</div>
    </body></html>`;
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
}

const TABS = [
    { key: 'daily',   label: 'Today' },
    { key: 'weekly',  label: 'This Week' },
    { key: 'monthly', label: 'This Month' },
    { key: 'custom',  label: 'Custom Range' },
];

export default function Reports() {
    const [tab, setTab] = useState('daily');
    const [from, setFrom] = useState(getToday);
    const [to, setTo]     = useState(getToday);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async (f, t) => {
        setLoading(true); setError(''); setData(null);
        try {
            const res = await getReports(f, t);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load report');
        } finally { setLoading(false); }
    }, []);

    const applyTab = (t) => {
        setTab(t);
        if (t !== 'custom') {
            const r = getRange(t);
            setFrom(r.from); setTo(r.to);
            load(r.from, r.to);
        }
    };

    useEffect(() => { const r = getRange('daily'); load(r.from, r.to); }, []);

    const s = data?.summary || {};

    return (
        <div style={{ padding: '24px 28px', maxWidth: 1100 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: NAVY, margin: 0 }}>📊 Reports</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '3px 0 0' }}>Patient visits & revenue by period</p>
                </div>
                {data && (
                    <button
                        onClick={() => printReport(data, from, to, TABS.find(t2 => t2.key === tab)?.label)}
                        style={{ padding: '9px 22px', background: NAVY, color: '#fff', border: 'none',
                            borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        🖨️ Print Report
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => applyTab(t.key)}
                        style={{ padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            fontSize: 13, fontWeight: 600,
                            background: tab === t.key ? NAVY : '#f1f5f9',
                            color: tab === t.key ? '#fff' : '#374151' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Date controls */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', border: '1px solid #e2e8f0',
                marginBottom: 22, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>From:</label>
                <input type="date" value={from}
                    onChange={e => { setFrom(e.target.value); if (tab !== 'custom') setTab('custom'); }}
                    style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>To:</label>
                <input type="date" value={to}
                    onChange={e => { setTo(e.target.value); if (tab !== 'custom') setTab('custom'); }}
                    style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                <button onClick={() => load(from, to)} disabled={loading}
                    style={{ padding: '8px 20px', background: loading ? '#9ca3af' : '#059669',
                        color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
                    {loading ? 'Loading…' : '🔍 Load Report'}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                    padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 18 }}>
                    ⚠️ {error}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b', fontSize: 15 }}>
                    Loading report…
                </div>
            )}

            {/* Report data */}
            {!loading && data && (
                <>
                    {/* Summary cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 }}>
                        <SummaryCard icon="👥" label="Total Visits" value={fmt(s.visits)} color="#0369a1" bg="#eff6ff" />
                        <SummaryCard icon="🆕" label="New Patients Registered" value={fmt(s.new_patients)} color="#059669" bg="#f0fdf4" />
                        <SummaryCard icon="💰" label="Consultation Revenue" value={fmtPKR(s.revenue)} color="#d97706" bg="#fffbeb" />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        {/* Doctor-wise table */}
                        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', gridColumn: '1 / -1' }}>
                            <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0',
                                fontWeight: 700, fontSize: 13, color: NAVY }}>
                                🩺 Doctor-wise Breakdown
                            </div>
                            {data.doctor_wise?.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc' }}>
                                            <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151' }}>Doctor</th>
                                            <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151' }}>Specialty</th>
                                            <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#374151' }}>Visits</th>
                                            <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#374151' }}>Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.doctor_wise.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 600 }}>{r.doctor_name || 'Walk-in / Unassigned'}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, color: '#64748b' }}>{r.specialty || '—'}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#0369a1' }}>{fmt(r.visits)}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#059669' }}>{fmtPKR(r.revenue)}</td>
                                            </tr>
                                        ))}
                                        {/* Totals row */}
                                        <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                                            <td colSpan={2} style={{ padding: '10px 14px', fontSize: 13, fontWeight: 800, color: NAVY }}>TOTAL</td>
                                            <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 800, textAlign: 'right', color: '#0369a1' }}>{fmt(s.visits)}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 800, textAlign: 'right', color: '#059669' }}>{fmtPKR(s.revenue)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
                                    No visits recorded in this period
                                </div>
                            )}
                        </div>

                        {/* Daily trend */}
                        {data.daily_trend?.length > 1 && (
                            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0',
                                    fontWeight: 700, fontSize: 13, color: NAVY }}>
                                    📈 Daily Trend
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc' }}>
                                            <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700 }}>Date</th>
                                            <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Visits</th>
                                            <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.daily_trend.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '8px 14px', fontSize: 12 }}>{r.date}</td>
                                                <td style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, textAlign: 'right', color: '#0369a1' }}>{r.visits}</td>
                                                <td style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, textAlign: 'right', color: '#059669' }}>{fmtPKR(r.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Gender breakdown */}
                        {data.gender_breakdown?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0',
                                    fontWeight: 700, fontSize: 13, color: NAVY }}>
                                    👤 Patient Gender Breakdown
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc' }}>
                                            <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700 }}>Gender</th>
                                            <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700 }}>Patients</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.gender_breakdown.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 14px', fontSize: 13 }}>{r.gender || 'Unknown'}</td>
                                                <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#7c3aed' }}>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Empty state */}
                    {s.visits === 0 && (
                        <div style={{ textAlign: 'center', padding: 48, color: '#64748b',
                            background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', marginTop: 16 }}>
                            <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
                            <p style={{ fontWeight: 600, marginBottom: 4 }}>No data for this period</p>
                            <p style={{ fontSize: 12 }}>Try a different date range</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
